// src/components/admin/UserList.jsx  
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";  
import { useNavigate, useSearchParams } from "react-router-dom";  
import { motion, AnimatePresence } from "framer-motion";  
import {  
  Users, Search, RefreshCw, X, MoreVertical, Shield, ScrollText,  
  Download, Trash2, Lock, Unlock, Square, ChevronLeft, ChevronRight,  
  UserPlus, Bot, AlertTriangle, UserCheck, Calendar, Eye, Play, Pause,  
} from "lucide-react";  
import api from "../../api";  
import AdminPageHeader from "./Shared/AdminPageHeader";  
import AdminStatCard from "./Shared/AdminStatCard";  
import AdminIconButton from "./Shared/AdminIconButton";  
import { AdminLoadingState, AdminErrorState, AdminEmptyState } from "./Shared/AdminStates";  
import CreateSuperuserModal from "./CreateSuperuser";  
import UserDetailDrawer from "./UserDetailDrawer";  
  
const PER_PAGE = 12;  
  
/* ═════ Small building blocks ═════ */  
  
const StatusBadge = ({ user }) => {  
  if (!user.is_active) return <Badge tone="gray" icon={Lock}>Inactive</Badge>;  
  if (user.is_superuser) return <Badge tone="purple" icon={Shield}>Admin</Badge>;  
  return <Badge tone="gold" icon={UserCheck}>Active</Badge>;  
};  
  
const Badge = ({ tone, icon: Icon, children }) => {  
  const tones = {  
    gray:   "bg-gray-500/10 text-gray-400 border-gray-700",  
    purple: "bg-purple-500/10 text-purple-300 border-purple-500/30",  
    gold:   "bg-gold/10 text-gold border-gold/30",  
    green:  "bg-green-500/10 text-green-400 border-green-500/30",  
    amber:  "bg-amber-500/10 text-amber-300 border-amber-500/40",  
    red:    "bg-red-500/10 text-red-400 border-red-500/30",  
  };  
  return (  
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-semibold rounded-md border uppercase tracking-wider ${tones[tone]}`}>  
      {Icon && <Icon size={10} />}  
      {children}  
    </span>  
  );  
};  
  
const AssetPill = ({ asset, onToggle, busy }) => {  
  // 4 states: RUNNING_OK / DRIFT / IDLE / NO_CREDS  
  const isRunning = asset.is_active && asset.task_alive;  
  const isDrift   = asset.is_active && !asset.task_alive;  
  const canStart  = asset.has_credentials && asset.is_available;  
  
  let cls, dot, title;  
  if (!canStart && !asset.is_active) {  
    cls = "bg-red-500/[0.06] border-red-500/25 text-red-400";  
    dot = "bg-red-400";  
    title = `Missing ${asset.requires_platform} credentials`;  
  } else if (isRunning) {  
    cls = "bg-green-500/10 border-green-500/30 text-green-400";  
    dot = "bg-green-400 animate-pulse";  
    title = "Running";  
  } else if (isDrift) {  
    cls = "bg-amber-500/10 border-amber-500/40 text-amber-300";  
    dot = "bg-amber-400";  
    title = "DB says active but task not alive — needs restart";  
  } else {  
    cls = "bg-gray-500/[0.06] border-gray-800 text-gray-400";  
    dot = "bg-gray-500";  
    title = "Idle";  
  }  
  
  return (  
    <div  
      title={title}  
      className={`inline-flex items-center gap-1.5 pl-1.5 pr-0.5 py-0.5 rounded border text-[10px] font-mono font-semibold ${cls}`}  
    >  
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />  
      <span>{asset.symbol}</span>  
      <button  
        onClick={(e) => { e.stopPropagation(); onToggle(asset); }}  
        disabled={busy || (!canStart && !asset.is_active)}  
        className="ml-0.5 p-0.5 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"  
        title={asset.is_active ? "Stop" : "Start"}  
      >  
        {busy ? <RefreshCw size={10} className="animate-spin" />  
              : asset.is_active ? <Pause size={10} /> : <Play size={10} />}  
      </button>  
    </div>  
  );  
};  
  
const MiniStat = ({ label, value, color = "text-white" }) => (  
  <div className="bg-black/30 border border-gray-800/60 rounded-lg px-2.5 py-2">  
    <p className="text-[9px] font-semibold uppercase tracking-wider text-gray-500 mb-0.5">{label}</p>  
    <p className={`text-sm font-bold leading-none ${color}`}>{value}</p>  
  </div>  
);  
  
/* ═════ Actions menu ═════ */  
  
const UserActionsMenu = ({ user, onAction, botActive }) => {  
  const [open, setOpen] = useState(false);  
  const ref = useRef(null);  
  useEffect(() => {  
    const h = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);  
    document.addEventListener("mousedown", h);  
    return () => document.removeEventListener("mousedown", h);  
  }, []);  
  
  const actions = [  
    { id: "view_detail", label: "Open detail", icon: Eye, color: "text-gray-300" },  
    { id: "view_trades", label: "View trades", icon: ScrollText, color: "text-gray-300" },  
    { id: "export_trades", label: "Export CSV", icon: Download, color: "text-gray-300",  
      show: (user.trading?.trade_count || 0) > 0 },  
    { divider: true, show: !user.is_superuser },  
    { id: user.is_active ? "deactivate" : "activate",  
      label: user.is_active ? "Deactivate account" : "Activate account",  
      icon: user.is_active ? Lock : Unlock,  
      color: user.is_active ? "text-yellow-400" : "text-green-400",  
      show: !user.is_superuser },  
    { id: "stop_all_bots", label: "Stop all bots", icon: Square,  
      color: "text-orange-400", show: botActive },  
    { divider: true, show: !user.is_superuser },  
    { id: "delete", label: "Delete user", icon: Trash2, color: "text-red-400",  
      show: !user.is_superuser },  
  ].filter((a) => a.show !== false);  
  
  return (  
    <div ref={ref} className="relative flex-shrink-0">  
      <button  
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}  
        className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.04] transition-all"  
      >  
        <MoreVertical size={16} />  
      </button>  
      <AnimatePresence>  
        {open && (  
          <motion.div  
            initial={{ opacity: 0, y: -4, scale: 0.97 }}  
            animate={{ opacity: 1, y: 0, scale: 1 }}  
            exit={{ opacity: 0, y: -4, scale: 0.97 }}  
            transition={{ duration: 0.12 }}  
            className="absolute right-0 mt-1 w-48 bg-gray-900/95 backdrop-blur-xl border border-gray-800 rounded-xl shadow-2xl z-30 overflow-hidden p-1"  
            onClick={(e) => e.stopPropagation()}  
          >  
            {actions.map((a, i) => a.divider ? (  
              <div key={`div-${i}`} className="my-1 border-t border-gray-800" />  
            ) : (  
              <button  
                key={a.id}  
                onClick={() => { setOpen(false); onAction(a.id, user); }}  
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/[0.04] transition-all text-xs font-medium ${a.color}`}  
              >  
                <a.icon size={13} />  
                <span>{a.label}</span>  
              </button>  
            ))}  
          </motion.div>  
        )}  
      </AnimatePresence>  
    </div>  
  );  
};  
  
/* ═════ User card ═════ */  
  
const UserCard = ({ user, onAction, onToggleAsset, togglingAssetId, onOpenDetail, index }) => {  
  const assets = user.assets || [];  
  const runningAssets = assets.filter((a) => a.is_active);  
  const driftedCount = assets.filter((a) => a.is_active && !a.task_alive).length;  
  const botActive = runningAssets.length > 0;  
  const pnl = user.trading?.total_pnl || 0;  
  const positive = pnl >= 0;  
  const winRate = user.trading?.win_rate || 0;  
  const initial = user.username?.charAt(0).toUpperCase() || "U";  
  
  const borderClass = !user.is_active  
    ? "border-gray-800/60 opacity-70"  
    : driftedCount > 0  
    ? "border-amber-500/40"  
    : botActive  
    ? "border-green-500/30"  
    : "border-gray-800 hover:border-gold/30";  
  
  return (  
    <motion.div  
      initial={{ opacity: 0, y: 8 }}  
      animate={{ opacity: 1, y: 0 }}  
      transition={{ delay: Math.min(index * 0.02, 0.3) }}  
      onClick={() => onOpenDetail(user)}  
      className={`relative bg-gradient-to-br from-gray-900/60 via-black/60 to-gray-900/60 backdrop-blur-xl border rounded-2xl p-4 transition-all cursor-pointer ${borderClass}`}  
    >  
      {/* Header */}  
      <div className="flex items-start gap-3 mb-3">  
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold text-sm flex-shrink-0 ${  
          user.is_superuser  
            ? "bg-purple-500/15 border border-purple-500/30 text-purple-300"  
            : "bg-gradient-to-br from-gold to-yellow-600 text-black"  
        }`}>  
          {initial}  
        </div>  
        <div className="flex-1 min-w-0">  
          <p className="text-sm font-semibold text-white truncate leading-tight">{user.username}</p>  
          <p className="text-[11px] text-gray-500 truncate mt-0.5">{user.email || "—"}</p>  
        </div>  
        <UserActionsMenu user={user} onAction={onAction} botActive={botActive} />  
      </div>  
  
      {/* Badges */}  
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">  
        <StatusBadge user={user} />  
        {user.credentials?.binance?.length > 0 && <Badge tone="gold">Binance</Badge>}  
        {user.credentials?.ig?.length > 0 && <Badge tone="gold">IG</Badge>}  
        {driftedCount > 0 && (  
          <Badge tone="amber" icon={AlertTriangle}>  
            {driftedCount} drift  
          </Badge>  
        )}  
      </div>  
  
      {/* Stats */}  
      <div className="grid grid-cols-3 gap-2 mb-3">  
        <MiniStat label="Trades" value={(user.trading?.trade_count || 0).toLocaleString()} />  
        <MiniStat  
          label="Net P&L"  
          value={`${positive ? "+" : "−"}$${Math.abs(pnl).toFixed(0)}`}  
          color={positive ? "text-green-400" : "text-red-400"}  
        />  
        <MiniStat  
          label="Win rate"  
          value={`${winRate.toFixed(0)}%`}  
          color={winRate >= 50 ? "text-green-400" : winRate >= 40 ? "text-yellow-400" : "text-red-400"}  
        />  
      </div>  
  
      {/* Asset pills — click to toggle right from the card */}  
      {assets.length > 0 && (  
        <div className="flex items-center gap-1.5 mb-3 flex-wrap">  
          {assets.map((a) => (  
            <AssetPill  
              key={a.registry_id}  
              asset={a}  
              busy={togglingAssetId === `${user.id}:${a.registry_id}`}  
              onToggle={(asset) => onToggleAsset(user, asset)}  
            />  
          ))}  
        </div>  
      )}  
  
      {/* Footer */}  
      <div className="flex items-center justify-between pt-3 border-t border-gray-800/60">  
        <div className="flex items-center gap-1.5 text-[11px] text-gray-500">  
          <Calendar size={11} />  
          <span>  
            {user.created_at  
              ? new Date(user.created_at).toLocaleDateString("en-US",  
                  { month: "short", day: "numeric", year: "numeric" })  
              : "—"}  
          </span>  
        </div>  
        <button  
          onClick={(e) => { e.stopPropagation(); onAction("view_trades", user); }}  
          className="text-[11px] font-semibold text-gold hover:text-yellow-300 transition-colors"  
        >  
          Trades →  
        </button>  
      </div>  
    </motion.div>  
  );  
};  
  
/* ═════ Filter bar ═════ */  
  
const FilterBar = ({ filters, setFilters, onSearch, loading }) => {  
  const options = [  
    { value: "all", label: "All" },  
    { value: "active", label: "Active" },  
    { value: "inactive", label: "Inactive" },  
    { value: "running", label: "Bots running" },  
    { value: "drift", label: "Drift" },  
    { value: "with_keys", label: "Has API" },  
  ];  
  
  return (  
    <motion.div  
      initial={{ opacity: 0, y: 6 }}  
      animate={{ opacity: 1, y: 0 }}  
      className="bg-black/30 border border-gray-800 rounded-2xl p-3 flex flex-col lg:flex-row gap-3"  
    >  
      <div className="flex-1 relative">  
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />  
        <input  
          type="text"  
          value={filters.search}  
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}  
          onKeyDown={(e) => e.key === "Enter" && onSearch()}  
          placeholder="Search by username or email…"  
          className="w-full pl-9 pr-9 py-2.5 bg-black/40 border border-gray-800 focus:border-gold/50 rounded-lg text-white text-sm placeholder-gray-600 outline-none transition-all"  
        />  
        {filters.search && (  
          <button  
            onClick={() => { setFilters({ ...filters, search: "" }); setTimeout(onSearch, 0); }}  
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"  
          >  
            <X size={14} />  
          </button>  
        )}  
      </div>  
  
      <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 lg:pb-0">  
        {options.map((o) => (  
          <button  
            key={o.value}  
            onClick={() => setFilters({ ...filters, status: o.value })}  
            className={`px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all border ${  
              filters.status === o.value  
                ? "bg-gold text-black border-gold shadow-md shadow-gold/20"  
                : "bg-black/40 text-gray-400 border-gray-800 hover:border-gold/40 hover:text-gold"  
            }`}  
          >  
            {o.label}  
          </button>  
        ))}  
      </div>  
  
      <button  
        onClick={onSearch}  
        disabled={loading}  
        className="px-4 py-2.5 bg-gold text-black font-semibold rounded-lg text-sm hover:bg-yellow-400 transition-all disabled:opacity-50 flex items-center gap-2"  
      >  
        {loading ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />}  
        Search  
      </button>  
    </motion.div>  
  );  
};  
  
/* ═════ Pagination ═════ */  
  
const Pagination = ({ page, totalPages, total, onChange }) => {  
  const pages = useMemo(() => {  
    const list = []; const show = 5;  
    if (totalPages <= show) for (let i = 1; i <= totalPages; i++) list.push(i);  
    else if (page <= 3) for (let i = 1; i <= show; i++) list.push(i);  
    else if (page >= totalPages - 2) for (let i = totalPages - show + 1; i <= totalPages; i++) list.push(i);  
    else for (let i = page - 2; i <= page + 2; i++) list.push(i);  
    return list;  
  }, [page, totalPages]);  
  
  const start = (page - 1) * PER_PAGE + 1;  
  const end = Math.min(page * PER_PAGE, total);  
  
  return (  
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 bg-black/30 border border-gray-800 rounded-xl">  
      <p className="text-xs text-gray-500">  
        <span className="text-white font-semibold">{start}</span>–  
        <span className="text-white font-semibold">{end}</span> of{" "}  
        <span className="text-white font-semibold">{total.toLocaleString()}</span>  
      </p>  
      <div className="flex items-center gap-1">  
        <button onClick={() => onChange(page - 1)} disabled={page === 1}  
          className="p-1.5 rounded-md border border-gray-800 text-gray-400 hover:border-gold/40 hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed transition-all">  
          <ChevronLeft size={14} />  
        </button>  
        {pages.map((p) => (  
          <button key={p} onClick={() => onChange(p)}  
            className={`w-8 h-8 rounded-md text-xs font-semibold transition-all ${  
              page === p ? "bg-gold text-black" : "text-gray-400 hover:bg-white/[0.04] hover:text-white"  
            }`}>{p}</button>  
        ))}  
        <button onClick={() => onChange(page + 1)} disabled={page === totalPages}  
          className="p-1.5 rounded-md border border-gray-800 text-gray-400 hover:border-gold/40 hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed transition-all">  
          <ChevronRight size={14} />  
        </button>  
      </div>  
    </div>  
  );  
};  
  
/* ═════ Confirm modal ═════ */  
  
const CONFIRM_CONFIGS = {  
  activate: {  
    title: "Activate account",  
    message: (u) => `${u.username} will be able to sign in and use the platform.`,  
    icon: Unlock, iconTone: "text-green-400", iconBg: "bg-green-500/10 border-green-500/30",  
    btn: "Activate", btnClass: "bg-green-500 hover:bg-green-400 text-black",  
  },  
  deactivate: {  
    title: "Deactivate account",  
    message: (u) => `${u.username} will lose access and all their bots will be stopped.`,  
    icon: Lock, iconTone: "text-yellow-400", iconBg: "bg-yellow-500/10 border-yellow-500/30",  
    btn: "Deactivate", btnClass: "bg-yellow-500 hover:bg-yellow-400 text-black",  
  },  
  stop_all_bots: {  
    title: "Stop all bots",  
    message: (u) => `All active bots for ${u.username} will be stopped immediately. Open positions are unaffected.`,  
    icon: Square, iconTone: "text-orange-400", iconBg: "bg-orange-500/10 border-orange-500/30",  
    btn: "Stop all bots", btnClass: "bg-orange-500 hover:bg-orange-400 text-black",  
  },  
  delete: {  
    title: "Delete user permanently",  
    message: (u) => `This will permanently delete ${u.username}'s account, trade history, and configurations. This cannot be undone.`,  
    icon: AlertTriangle, iconTone: "text-red-400", iconBg: "bg-red-500/10 border-red-500/30",  
    btn: "Delete permanently", btnClass: "bg-red-500 hover:bg-red-400 text-white",  
  },  
};  
  
const ConfirmModal = ({ open, action, user, loading, onClose, onConfirm }) => {  
  if (!open || !user || !action) return null;  
  const cfg = CONFIRM_CONFIGS[action];  
  const Icon = cfg.icon;  
  return (  
    <AnimatePresence>  
      <motion.div  
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}  
        onClick={onClose}  
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"  
      >  
        <motion.div  
          initial={{ opacity: 0, scale: 0.96, y: 8 }}  
          animate={{ opacity: 1, scale: 1, y: 0 }}  
          exit={{ opacity: 0, scale: 0.96, y: 8 }}  
          onClick={(e) => e.stopPropagation()}  
          className="bg-gray-900/95 backdrop-blur-xl border border-gray-800 rounded-2xl p-6 max-w-md w-full shadow-2xl"  
        >  
          <div className="flex items-start gap-4 mb-5">  
            <div className={`w-11 h-11 rounded-xl border flex items-center justify-center flex-shrink-0 ${cfg.iconBg}`}>  
              <Icon size={20} className={cfg.iconTone} />  
            </div>  
            <div className="flex-1 min-w-0">  
              <h3 className="text-base font-bold text-white mb-1">{cfg.title}</h3>  
              <p className="text-sm text-gray-400 leading-relaxed">{cfg.message(user)}</p>  
            </div>  
          </div>  
          <div className="flex items-center justify-end gap-2">  
            <button onClick={onClose} disabled={loading}  
              className="px-4 py-2 text-sm font-semibold text-gray-400 hover:text-white hover:bg-white/[0.04] rounded-lg transition-all disabled:opacity-50">  
              Cancel  
            </button>  
            <button onClick={onConfirm} disabled={loading}  
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all disabled:opacity-50 flex items-center gap-2 ${cfg.btnClass}`}>  
              {loading ? <><RefreshCw size={13} className="animate-spin" /> Processing…</>  
                       : <><Icon size={13} /> {cfg.btn}</>}  
            </button>  
          </div>  
        </motion.div>  
      </motion.div>  
    </AnimatePresence>  
  );  
};  
  
/* ═════════════════════════════════════════════  
   MAIN  
   ═════════════════════════════════════════════ */  
  
const UserList = () => {  
  const navigate = useNavigate();  
  const [searchParams, setSearchParams] = useSearchParams();  
  
  const [payload, setPayload] = useState(null);  
  const [loading, setLoading] = useState(true);  
  const [error, setError] = useState(null);  
  const [page, setPage] = useState(1);  
  
  const [filters, setFilters] = useState({  
    search: searchParams.get("search") || "",  
    status: searchParams.get("status") || "all",  
  });  
  
  const [confirmAction, setConfirmAction] = useState(null);  
  const [selectedUser, setSelectedUser] = useState(null);  
  const [actionLoading, setActionLoading] = useState(false);  
  const [createOpen, setCreateOpen] = useState(false);  
  const [drawerUserId, setDrawerUserId] = useState(null);  
  const [togglingAssetId, setTogglingAssetId] = useState(null);  
  
  const fetchUsers = useCallback(async () => {  
    try {  
      setLoading(true);  
      setError(null);  
      const params = new URLSearchParams();  
      if (filters.search) params.append("search", filters.search);  
      if (filters.status === "running") params.append("only_running", "true");  
      if (filters.status === "with_keys") params.append("only_with_keys", "true");  
      if (filters.status === "active") params.append("only_running", "false"); // best-effort  
      params.append("page", page);  
      params.append("limit", PER_PAGE);  
  
      const res = await api.get(`/admin/dashboard/overview?${params.toString()}`);  
      setPayload(res.data);  
    } catch (err) {  
      console.error("[UserList]", err);  
      setError(err.response?.data?.detail || "Failed to load users.");  
    } finally {  
      setLoading(false);  
    }  
  }, [filters.search, filters.status, page]);  
  
  useEffect(() => { fetchUsers(); }, [fetchUsers]);  
  
  useEffect(() => {  
    const p = new URLSearchParams();  
    if (filters.search) p.set("search", filters.search);  
    if (filters.status !== "all") p.set("status", filters.status);  
    setSearchParams(p, { replace: true });  
  }, [filters, setSearchParams]);  
  
  // Client-side sub-filters (backend doesn't support these directly)  
  const rows = useMemo(() => {  
    let list = payload?.rows || [];  
    if (filters.status === "inactive") list = list.filter((u) => !u.is_active);  
    if (filters.status === "drift") {  
      list = list.filter((u) =>  
        (u.assets || []).some((a) => a.is_active && !a.task_alive));  
    }  
    return list;  
  }, [payload, filters.status]);  
  
  const counters = payload?.counters;  
  const pagination = payload?.pagination;  
  
  const totalUsers   = counters?.total_users ?? 0;  
  const runningTotal = counters?.running_configs ?? 0;  
  const driftCount   = useMemo(() =>  
    (payload?.rows || []).reduce((n, u) =>  
      n + (u.assets || []).filter((a) => a.is_active && !a.task_alive).length, 0),  
    [payload]);  
  
  const handleAction = async (action, user) => {  
    setSelectedUser(user);  
    if (action === "view_detail") { setDrawerUserId(user.id); return; }  
    if (action === "view_trades") {  
      navigate(`/admin/trade-logs?user_id=${user.id}&username=${encodeURIComponent(user.username)}`);  
      return;  
    }  
    if (action === "export_trades") {  
      try {  
        const res = await api.get(`/admin/export-trades/${user.id}`, { responseType: "blob" });  
        const url = window.URL.createObjectURL(new Blob([res.data]));  
        const link = document.createElement("a");  
        link.href = url;  
        link.download = `${user.username}_trades_${new Date().toISOString().split("T")[0]}.csv`;  
        document.body.appendChild(link); link.click(); link.remove();  
        window.URL.revokeObjectURL(url);  
      } catch (err) {  
        alert("Export failed: " + (err.response?.data?.detail || err.message));  
      }  
      return;  
    }  
    setConfirmAction(action);  
  };  
  
  const executeAction = async () => {  
    if (!selectedUser || !confirmAction) return;  
    try {  
      setActionLoading(true);  
      switch (confirmAction) {  
        case "activate":  
          await api.patch(`/admin/users/${selectedUser.id}/status?is_active=true`); break;  
        case "deactivate":  
          try { await api.post(`/admin/users/${selectedUser.id}/bots/stop-all`); } catch {}  
          await api.patch(`/admin/users/${selectedUser.id}/status?is_active=false`); break;  
        case "stop_all_bots":  
          await api.post(`/admin/users/${selectedUser.id}/bots/stop-all`); break;  
        case "delete":  
          await api.delete(`/admin/delete-user/${selectedUser.id}`); break;  
        default: break;  
      }  
      await fetchUsers();  
      setConfirmAction(null); setSelectedUser(null);  
    } catch (err) {  
      alert(`Failed: ${err.response?.data?.detail || err.message}`);  
    } finally {  
      setActionLoading(false);  
    }  
  };  
  
  const toggleAsset = async (user, asset) => {  
    const key = `${user.id}:${asset.registry_id}`;  
    setTogglingAssetId(key);  
    try {  
      const url = asset.is_active  
        ? `/admin/users/${user.id}/assets/${asset.registry_id}/deactivate`  
        : `/admin/users/${user.id}/assets/${asset.registry_id}/activate`;  
      await api.post(url);  
      await fetchUsers();  
    } catch (err) {  
      alert(`${asset.symbol}: ${err.response?.data?.detail || err.message}`);  
    } finally {  
      setTogglingAssetId(null);  
    }  
  };  
  
  return (  
    <div className="space-y-6">  
      <AdminPageHeader  
        title="Users"  
        subtitle={`${totalUsers.toLocaleString()} registered ${totalUsers === 1 ? "user" : "users"}`}  
        actions={  
          <>  
            <AdminIconButton onClick={fetchUsers} disabled={loading} title="Refresh">  
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />  
            </AdminIconButton>  
            <button  
              onClick={() => setCreateOpen(true)}  
              className="inline-flex items-center gap-2 px-3.5 h-10 bg-gold hover:bg-yellow-400 text-black text-xs font-bold rounded-lg transition-all shadow-md shadow-gold/20 uppercase tracking-wider"  
            >  
              <UserPlus size={14} />  
              <span className="hidden sm:inline">New admin</span>  
            </button>  
          </>  
        }  
      />  
  
      {/* Stats — all from server counters */}  
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">  
        <AdminStatCard label="Total users" value={totalUsers.toLocaleString()} index={0} />  
        <AdminStatCard  
          label="Bots running"  
          value={runningTotal.toLocaleString()}  
          valueColor={runningTotal > 0 ? "text-green-400" : "text-white"}  
          sub={Object.entries(counters?.running_per_asset || {})  
            .map(([k, v]) => `${k} ${v}`).join(" · ") || "None"}  
          index={1}  
        />  
        <AdminStatCard  
          label="Drifted configs"  
          value={driftCount.toLocaleString()}  
          valueColor={driftCount > 0 ? "text-amber-400" : "text-white"}  
          sub={driftCount > 0 ? "Need investigation" : "All healthy"}  
          index={2}  
        />  
        <AdminStatCard  
          label="With credentials"  
          value={(counters?.users_with_binance + counters?.users_with_ig || 0).toLocaleString()}  
          sub={`Binance ${counters?.users_with_binance || 0} · IG ${counters?.users_with_ig || 0}`}  
          index={3}  
        />  
      </div>  
  
      <FilterBar  
        filters={filters}  
        setFilters={setFilters}  
        onSearch={() => { setPage(1); fetchUsers(); }}  
        loading={loading}  
      />  
  
      {loading ? (  
        <AdminLoadingState label="Loading users…" />  
      ) : error ? (  
        <AdminErrorState message={error} onRetry={fetchUsers} />  
      ) : rows.length === 0 ? (  
        <AdminEmptyState  
          icon={Users}  
          title="No users found"  
          message={filters.search || filters.status !== "all"  
            ? "Try adjusting your search or filter criteria."  
            : "Users will appear here once they sign up."}  
          action={(filters.search || filters.status !== "all") && (  
            <button  
              onClick={() => { setFilters({ search: "", status: "all" }); setPage(1); }}  
              className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/40 text-gold hover:bg-gold/20 text-xs font-semibold rounded-lg transition-all"  
            >  
              Clear filters  
            </button>  
          )}  
        />  
      ) : (  
        <>  
          <motion.div  
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}  
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4"  
          >  
            {rows.map((u, i) => (  
              <UserCard  
                key={u.id}  
                user={u}  
                index={i}  
                onAction={handleAction}  
                onToggleAsset={toggleAsset}  
                togglingAssetId={togglingAssetId}  
                onOpenDetail={(user) => setDrawerUserId(user.id)}  
              />  
            ))}  
          </motion.div>  
  
          {pagination?.pages > 1 && (  
            <Pagination  
              page={pagination.page}  
              totalPages={pagination.pages}  
              total={pagination.total}  
              onChange={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); }}  
            />  
          )}  
        </>  
      )}  
  
      <ConfirmModal  
        open={!!confirmAction}  
        action={confirmAction}  
        user={selectedUser}  
        loading={actionLoading}  
        onClose={() => { setConfirmAction(null); setSelectedUser(null); }}  
        onConfirm={executeAction}  
      />  
  
      <CreateSuperuserModal  
        open={createOpen}  
        onClose={() => setCreateOpen(false)}  
        onCreated={() => { setCreateOpen(false); fetchUsers(); }}  
      />  
  
      <UserDetailDrawer  
        userId={drawerUserId}  
        onClose={() => setDrawerUserId(null)}  
        onChanged={fetchUsers}  
      />  
    </div>  
  );  
};  
  
export default UserList;  